import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AdminUser, ApiService, Employer } from '../../core/api.service';

@Component({
  selector: 'ch-employer-detail',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './employer-detail.component.html'
})
export class EmployerDetailComponent implements OnInit {
  employer: Employer | null = null;
  loading = false;
  saving = false;
  creatingEmployerUser = false;
  editingEmployer = false;
  showingCreateEmployerUser = false;
  error = '';
  notice = '';
  employerUsers: AdminUser[] = [];
  loadingEmployerUsers = false;
  uploading: Record<string, boolean> = {};
  viewing: Record<string, boolean> = {};
  employerUserForm = {
    full_name: '',
    email: '',
    role: 'employer_admin',
    status: 'active'
  };

  constructor(private readonly api: ApiService, private readonly route: ActivatedRoute) {}

  ngOnInit(): void {
    this.load();
  }

  async load(): Promise<void> {
    const employerID = this.route.snapshot.paramMap.get('id') || '';
    if (!employerID) {
      this.error = 'Employer not found.';
      return;
    }
    this.loading = true;
    this.error = '';
    try {
      this.employer = await this.api.employerDetail(employerID);
      await this.loadEmployerUsers();
    } catch {
      this.error = 'Could not load employer.';
    } finally {
      this.loading = false;
    }
  }

  async loadEmployerUsers(): Promise<void> {
    if (!this.employer || this.loadingEmployerUsers) return;
    this.loadingEmployerUsers = true;
    try {
      const result = await this.api.employerUsers(this.employer.id);
      this.employerUsers = result.items;
    } catch {
      this.error = 'Could not load employer users.';
    } finally {
      this.loadingEmployerUsers = false;
    }
  }

  async save(): Promise<void> {
    if (!this.employer || this.saving) return;
    this.saving = true;
    this.error = '';
    this.notice = '';
    try {
      this.employer = await this.api.updateEmployer(this.employer.id, this.employer);
      this.notice = this.employer.status === 'active'
        ? 'Employer is now active.'
        : 'Employer updated. It will activate after all required documents are uploaded.';
      this.editingEmployer = false;
    } catch {
      this.error = 'Could not update employer.';
    } finally {
      this.saving = false;
    }
  }

  async uploadDocument(event: Event, field: keyof Employer, purpose: string): Promise<void> {
    if (!this.employer) return;
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    this.uploading[field] = true;
    this.error = '';
    this.notice = '';
    try {
      const fileURL = await this.api.uploadFileViaAPI(purpose, file);
      this.employer = await this.api.updateEmployer(this.employer.id, { [field]: fileURL } as Partial<Employer>);
      this.notice = 'Document uploaded and employer status rechecked.';
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'Could not upload document.';
    } finally {
      this.uploading[field] = false;
    }
  }

  async viewDocument(fileURL: string, field: string): Promise<void> {
    if (!fileURL) return;
    this.viewing[field] = true;
    this.error = '';
    try {
      const viewURL = await this.api.documentViewURL(fileURL);
      window.open(viewURL, '_blank', 'noopener,noreferrer');
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'Could not open document.';
    } finally {
      this.viewing[field] = false;
    }
  }

  async createEmployerUser(): Promise<void> {
    if (!this.employer || !this.employerUserForm.full_name.trim() || !this.employerUserForm.email.trim() || this.creatingEmployerUser) return;
    this.creatingEmployerUser = true;
    this.error = '';
    this.notice = '';
    try {
      await this.api.createEmployerUser({
        employer_id: this.employer.id,
        full_name: this.employerUserForm.full_name.trim(),
        email: this.employerUserForm.email.trim().toLowerCase(),
        role: this.employerUserForm.role,
        status: this.employerUserForm.status
      });
      this.employerUserForm = { full_name: '', email: '', role: 'employer_admin', status: 'active' };
      this.showingCreateEmployerUser = false;
      this.notice = 'Employer login created. A temporary password has been sent by email.';
      await this.loadEmployerUsers();
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'Could not create employer login.';
    } finally {
      this.creatingEmployerUser = false;
    }
  }

  cancelEmployerEdit(): void {
    this.editingEmployer = false;
    this.load();
  }
}
